import { BadgeCheck, Star } from 'lucide-react';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import { formatMapleMarketPrice } from '@/data/mapleMarketApps';

interface MapleMarketAppCardProps {
  app: MapleMarketApp;
  owned?: boolean;
  variant?: 'default' | 'wide';
  onSelectApp: (appId: string) => void;
}

export default function MapleMarketAppCard({
  app,
  owned = false,
  variant = 'default',
  onSelectApp,
}: MapleMarketAppCardProps) {
  if (variant === 'wide') {
    return (
      <button
        type="button"
        onClick={() => onSelectApp(app.id)}
        className="group flex w-full gap-4 rounded-xl border border-border bg-card p-3 text-left shadow-card transition-all duration-300 hover:border-primary/30 hover:shadow-glow"
      >
        <img
          src={app.image}
          alt={app.name}
          loading="lazy"
          width={768}
          height={512}
          className="h-24 w-36 shrink-0 rounded-lg object-cover"
        />
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="truncate text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                {app.name}
              </h3>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{app.description}</p>
            </div>
            {owned && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                <BadgeCheck className="h-3.5 w-3.5" />
                Owned
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-primary text-primary" />
              <span className="text-xs font-medium text-foreground">{app.rating}</span>
            </div>
            <span className="text-xs font-semibold text-primary">{formatMapleMarketPrice(app.price)}</span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelectApp(app.id)}
      className="group flex w-full flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-card transition-all duration-300 hover:border-primary/30 hover:shadow-glow"
    >
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={app.image}
          alt={app.name}
          loading="lazy"
          width={768}
          height={512}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-2 top-2 flex flex-wrap gap-2">
          {owned && (
            <span className="inline-flex items-center gap-1 rounded bg-primary/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
              <BadgeCheck className="h-3 w-3" />
              Owned
            </span>
          )}
          {app.price === 'Free' && (
            <span className="rounded bg-secondary/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-foreground">
              Free
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="truncate text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
          {app.name}
        </h3>
        <p className="mt-1 line-clamp-2 flex-1 text-xs text-muted-foreground">{app.description}</p>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-primary text-primary" />
            <span className="text-xs font-medium text-foreground">{app.rating}</span>
            <span className="text-xs text-muted-foreground">({app.reviews.toLocaleString()})</span>
          </div>
          <span className="text-sm font-bold text-primary">{formatMapleMarketPrice(app.price)}</span>
        </div>
      </div>
    </button>
  );
}
